import {
  HTML_HREF_ATTRIBUTE,
  HTML_IMAGE_TAG,
  HTML_LINK_TAG,
  HTML_SRC_ATTRIBUTE,
  UNTRUSTED_HTML_ALLOWED_ATTRIBUTES,
  UNTRUSTED_HTML_ALLOWED_LINK_SCHEMES,
  UNTRUSTED_HTML_ALLOWED_TAGS,
  UNTRUSTED_HTML_DROPPED_WITH_CONTENT_TAGS,
} from '@this+that/constants';
import DOMPurify from 'dompurify';

import { getUrlHost } from './getUrlHost';
import { hardenAnchor, TRUSTED_LINK_STYLE } from './linkHardening';
import { isAllowedInlineImageSource, isAllowedLinkUrl } from './untrustedUrl';

export interface UntrustedHtmlOptions {
  formatImageLabel?: (host: string) => string;
}

const PLACEHOLDER_TAG = 'span';
const STYLE_ATTRIBUTE = 'style';
const CONTENT_ID_URI_SCHEME = 'cid';
const ALLOWED_ATTRIBUTE_NAMES = [
  ...new Set(
    Object.values(UNTRUSTED_HTML_ALLOWED_ATTRIBUTES).flatMap((attributes) => [
      ...attributes,
    ]),
  ),
];
const ALLOWED_URI_REGEXP = new RegExp(
  `^(?:(?:${[...UNTRUSTED_HTML_ALLOWED_LINK_SCHEMES, CONTENT_ID_URI_SCHEME].join('|')}):|[^a-z]|[a-z+.-]+(?:[^a-z+.\\-:]|$))`,
  'i',
);

const isAttributeAllowed = (
  tag: string,
  attribute: string,
  value: string,
): boolean => {
  if (!UNTRUSTED_HTML_ALLOWED_ATTRIBUTES[tag]?.has(attribute)) {
    return false;
  }
  return attribute !== HTML_HREF_ATTRIBUTE || isAllowedLinkUrl(value);
};

const restrictAttributes = (
  node: Node,
  data: { attrName: string; attrValue: string; keepAttr: boolean },
) => {
  const tag = node.nodeName.toLowerCase();
  if (!isAttributeAllowed(tag, data.attrName.toLowerCase(), data.attrValue)) {
    data.keepAttr = false;
  }
};

const hardenLink = (node: Element) => {
  if (node.nodeName.toLowerCase() === HTML_LINK_TAG) {
    hardenAnchor(node);
    node.setAttribute(STYLE_ATTRIBUTE, TRUSTED_LINK_STYLE);
  }
};

const replaceRemoteImages = (
  root: ParentNode,
  formatImageLabel?: (host: string) => string,
) => {
  for (const img of Array.from(root.querySelectorAll(HTML_IMAGE_TAG))) {
    const src = (img.getAttribute(HTML_SRC_ATTRIBUTE) ?? '').trim();
    if (isAllowedInlineImageSource(src)) {
      continue;
    }
    if (!formatImageLabel) {
      img.remove();
      continue;
    }
    const placeholder = img.ownerDocument.createElement(PLACEHOLDER_TAG);
    placeholder.textContent = formatImageLabel(getUrlHost(src));
    img.replaceWith(placeholder);
  }
};

export const sanitizeUntrustedHtml = (
  content: string,
  { formatImageLabel }: UntrustedHtmlOptions,
): string => {
  DOMPurify.addHook('uponSanitizeAttribute', restrictAttributes);
  DOMPurify.addHook('afterSanitizeAttributes', hardenLink);

  try {
    const fragment = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [...UNTRUSTED_HTML_ALLOWED_TAGS],
      ALLOWED_ATTR: ALLOWED_ATTRIBUTE_NAMES,
      ADD_FORBID_CONTENTS: [...UNTRUSTED_HTML_DROPPED_WITH_CONTENT_TAGS],
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
      ALLOWED_URI_REGEXP,
      RETURN_DOM_FRAGMENT: true,
    });
    if (!fragment) {
      return '';
    }
    replaceRemoteImages(fragment, formatImageLabel);
    const container = fragment.ownerDocument.createElement('div');
    container.append(fragment);
    return container.innerHTML;
  } finally {
    DOMPurify.removeAllHooks();
  }
};
